export type CookieConsent = {
  necessary: true;
  timestamp: number;
};

const CONSENT_STORAGE_KEY = 'dialup_cookie_consent';
const CONSENT_EXPIRY_DAYS = 365;

const normalizeStoredConsent = (stored: unknown): CookieConsent | null => {
  if (typeof stored !== 'object' || stored == null) return null;

  const candidate = stored as {
    necessary?: boolean;
    timestamp?: number;
  };

  if (typeof candidate.necessary !== 'boolean') return null;

  return {
    necessary: true,
    timestamp:
      typeof candidate.timestamp === 'number'
        ? candidate.timestamp
        : Date.now(),
  };
};

export const getCookieConsent = (): CookieConsent | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as unknown;
    const consent = normalizeStoredConsent(parsed);
    if (!consent) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }

    const expiryDate =
      consent.timestamp + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    // Check if consent has expired
    if (Date.now() > expiryDate) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }

    return consent;
  } catch {
    return null;
  }
};

export const setCookieConsent = (_consent?: Partial<CookieConsent>): void => {
  if (typeof window === 'undefined') return;

  const fullConsent: CookieConsent = {
    ..._consent,
    necessary: true, // Always true, required for site functionality
    timestamp: Date.now(),
  };

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(fullConsent));
  } catch (error) {
    console.error('Failed to save cookie consent:', error);
  }
};

export const hasConsent = (): boolean => {
  return getCookieConsent() !== null;
};

export const clearCookieConsent = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(CONSENT_STORAGE_KEY);
};
